import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Municipio } from '../../../../../shared/models/response/localidade.model';
import { CardCollapseComponent } from '../../../../../shared/components/collapse/card-collapse.component';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { CommonModule } from '@angular/common';
import { TableFilterBarComponent } from '../../../../../shared/components/table-filter-bar/table-filter-bar.component';
import { CadastroReservaMunicipio } from '../../../../../shared/models/response/cadastro-reserva-municipio.model';
import { ComprovanteConteudo } from '../../../historico/historico-confirmacao-vaga.model';
import { LoadingService } from '../../../../../shared/services/loading.service';
import { CadastroReservaMunicipioService } from '../../../../../shared/services/cadastro-reserva-municipio.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  DESLIGAMENTO_PDF_HEADER_FOOTER_STYLES,
  DesligamentoPdfLayout,
} from '../../../../desligamento-profissional/desligamento-pdf-layout.util';

const TITULO_COMPROVANTE = 'Comprovante de Confirmação de Vagas';

@Component({
  selector: 'app-resumo-confirmacao-print',
  imports: [CardCollapseComponent, CommonModule, TableFilterBarComponent],
  templateUrl: './resumo-confirmacao-print.component.html',
  styleUrl: './resumo-confirmacao-print.component.scss',
})
export class ResumoConfirmacaoPrint implements OnInit {
  @Input() municpio!: Municipio;
  @Input() confirmacaoVaga: any;
  @Input() cadastrosReservaMunicipio: CadastroReservaMunicipio[] = [];
  @Input() modalRef: any;
  @Input() data: any[] = [];
  @Input() resumoVagas: any[] = [];
  @Input() totalVagasConfirmadas: number = 0;
  /** Conteúdo congelado de um comprovante emitido (PGPP-756): reproduz o PDF daquela versão sem consultar a API. */
  @Input() comprovante: ComprovanteConteudo | null = null;
  @ViewChild('printSection') printSection!: any;

  private readonly loadingService = inject(LoadingService);
  private readonly cadastroReservaMunicipioService = inject(
    CadastroReservaMunicipioService,
  );
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);

  cabecalhoHtml: SafeHtml | null = null;
  rodapeHtml: SafeHtml | null = null;

  ngOnInit(): void {
    this.initialize().then(() => {
      this.montarCabecalhoERodape();
      this.cdr.detectChanges();
      setTimeout(() => {
        this.print();
        this.modalRef.close();
      }, 100);
    });
  }

  print(): void {
    const pages = document.querySelector('#printSection') as HTMLElement;

    let pdf = new jsPDF({
      unit: 'px',
      format: [210, 297],
    });
    pdf.html(pages, {
      callback: (pdf: jsPDF) => {
        pdf.save(this.nomeArquivo());
      },
      html2canvas: {
        scale: 0.25,
      },
      margin: [5, 5, 5, 5],
      autoPaging: 'text',
    });
  }

  // Layout padrão dos PDFs do PGP, sem logomarcas (período de defeso eleitoral — PGPP-730).
  // O <style> vai junto do cabeçalho porque o conteúdo de innerHTML não recebe os estilos do
  // componente; fica aninhado em #printSection porque o header do app também usa .header-top.
  // O pdf.html() do jsPDF não desenha linear-gradient (a faixa saía preta): faixa em cor sólida.
  private montarCabecalhoERodape(): void {
    const ciclo = this.confirmacaoVaga?.ciclo;
    const municipio = this.confirmacaoVaga?.municipio;
    const subtitulo = [
      ciclo?.programa?.nome,
      ciclo ? `Ciclo do Edital ${('00' + ciclo.numeroCiclo).slice(-3)}/${ciclo.anoCiclo}` : null,
      municipio ? `${municipio.nome}/${municipio.siglaUf}` : null,
    ]
      .filter(Boolean)
      .join(' · ');

    this.cabecalhoHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<style>#printSection { ${DESLIGAMENTO_PDF_HEADER_FOOTER_STYLES} .header-stripe { background: #0F8EED; } }</style>` +
        DesligamentoPdfLayout.montarCabecalho(TITULO_COMPROVANTE, subtitulo),
    );
    this.rodapeHtml = this.sanitizer.bypassSecurityTrustHtml(DesligamentoPdfLayout.montarRodape('1', '1'));
  }

  private nomeArquivo(): string {
    if (!this.comprovante) {
      return 'resumo-confirmacao.pdf';
    }
    const data = (this.comprovante.dataConfirmacao ?? '').replace(/[-:T]/g, '').slice(0, 12);
    const tipo = { CR: 'cadastro-reserva', CA: 'confirmacao-aprimoramento', CV: 'confirmacao-vagas' }[this.comprovante.tipo];
    return `comprovante-${tipo}-${data}.pdf`;
  }

  // Monta os mesmos dados que a tela de resumo passa, a partir da foto gravada na efetivação.
  private aplicarComprovante(comprovante: ComprovanteConteudo): void {
    this.confirmacaoVaga = {
      ciclo: {
        numeroCiclo: comprovante.ciclo.numeroCiclo,
        anoCiclo: comprovante.ciclo.anoCiclo,
        programa: { nome: comprovante.ciclo.programa },
      },
      municipio: comprovante.municipio,
      uf: comprovante.uf ?? null,
      nivelVaga: comprovante.municipio ? 'Municipal' : 'Estadual',
      usuario: { nome: comprovante.gestor },
      dataConfirmacao: comprovante.dataConfirmacao,
    };
    this.totalVagasConfirmadas = comprovante.totalVagasConfirmadas;
    this.resumoVagas = comprovante.resumo.map((r) => ({ modalidade: r.modalidade, total: r.total }));

    const porModalidade = new Map<string, any[]>();
    comprovante.vagas.forEach((vaga) => {
      const modalidade = vaga.modalidade ?? '-';
      const linha = {
        id: vaga.nuIdVaga,
        nuIdVaga: vaga.nuIdVaga,
        coCnes: vaga.coCnes,
        nomeEstabelecimento: vaga.nomeEstabelecimento,
        vagaCicloIne: vaga.nuIne
          ? {
              nuIne: vaga.nuIne,
              nomeEquipe: vaga.nomeEquipe,
              tipoEquipe: { nome: vaga.tipoEquipe },
              coCnes: vaga.coCnes,
              nomeEstabelecimento: vaga.nomeEstabelecimento,
            }
          : null,
        vagaCiclo: { vagaPrograma: { tipoEquipe: { nome: vaga.tipoEquipe } } },
      };
      porModalidade.set(modalidade, [...(porModalidade.get(modalidade) ?? []), linha]);
    });
    this.data = Array.from(porModalidade.entries()).map(([modalidade, vagas]) => ({
      modalidadeId: modalidade,
      modalidade,
      pages: [{ page: 1, data: vagas }],
      currentPage: 1,
      total: vagas.length,
    }));

    this.cadastrosReservaMunicipio = comprovante.cadastroReserva.map(
      (cadastro, indice) =>
        ({ id: indice, tipoEquipe: { nome: cadastro.tipoEquipe }, stConfirmado: cadastro.confirmado }) as unknown as CadastroReservaMunicipio,
    );
  }

  private async initialize(): Promise<void> {
    return new Promise(async (resolve) => {
      if (this.comprovante) {
        this.aplicarComprovante(this.comprovante);
      } else if (this.confirmacaoVaga) {
        await this.loadReserva(this.confirmacaoVaga.ciclo.id);
      } else {
        this.confirmacaoVaga = {
          ciclo: this.cadastrosReservaMunicipio[0]?.ciclo,
          municipio: this.cadastrosReservaMunicipio[0]?.municipio,
          usuario: this.cadastrosReservaMunicipio[0]?.usuario,
        };
      }

      resolve();
    });
  }

  private loadReserva(cicloId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.loadingService.open();
      this.cadastroReservaMunicipioService
        .fetchByMunicipioAndCiclo(this.municpio?.id as string, cicloId)
        .subscribe({
          next: (cadastros: CadastroReservaMunicipio[]) => {
            this.loadingService.close();
            this.cadastrosReservaMunicipio = cadastros;
            // Handle reserva data as needed
            this.cdr.detectChanges();
            resolve();
          },
          error: (error) => {
            this.loadingService.close();
            reject();
          },
        });
    });
  }
}
